import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Send } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const OK = ["paid", "payment_submitted", "confirmed", "completed", "unpaid"];

const payLineForDoctor = (b) =>
  b.status === "unpaid"
    ? `Payment: Rs ${b.amount} - NOT DONE (person chose to pay later)`
    : `Payment: Rs ${b.amount} via ${b.payment?.method === "cashfree" ? "Online (Cashfree)" : "UPI/QR direct"} - Ref ${b.payment?.ref || "-"}`;

const statusLine = (b) =>
  ["paid", "confirmed", "completed"].includes(b.status)
    ? "Payment status: Completed"
    : b.status === "payment_submitted"
      ? "Payment status: Submitted (to verify)"
      : b.status === "unpaid"
        ? "Payment status: Not done"
        : `Payment status: ${b.status}`;

export const buildWhatsappText = (prefill, b) =>
  [
    prefill,
    "",
    `Booking ref: ${b.ref}`,
    `Name: ${b.name}`,
    `Age: ${b.age}`,
    `Gender: ${b.gender.charAt(0).toUpperCase()}${b.gender.slice(1)}`,
    `Address: ${b.address}`,
    `WhatsApp: ${b.phone}`,
    `Occupation / Education: ${b.occupation}`,
    `Type of help: ${b.service_label}`,
    `Appointment: ${b.date} at ${b.time}`,
    payLineForDoctor(b),
    statusLine(b),
  ].join("\n");

function PatientConfirmation({ b, settings, t }) {
  const paid = ["paid", "confirmed", "completed"].includes(b.status);
  const submitted = b.status === "payment_submitted";
  const unpaid = b.status === "unpaid";
  const waDoctor = `https://wa.me/${settings.whatsapp_number}`;
  const amount = b.amount;
  const paymentMode = paid
    ? (b.payment?.method === "cashfree" ? t("payModeOnline") : t("payModeUpi"))
    : submitted
      ? t("payModeUpiPending")
      : t("payModeNotDone");
  return (
    <div className="rounded-3xl border border-emerald-300/25 bg-black/25 p-5 sm:p-6" data-testid="patient-confirmation">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-200/80">{t("yourConfirmation")}</p>
      <p className="mt-2 text-sm leading-relaxed text-emerald-50">
        {t("dearName")} <b className="text-white">{b.name}</b>,<br />
        {t("bookingSuccessful")}
      </p>
      <ul className="mt-3 space-y-1 text-sm text-emerald-50/90">
        <li>• Ref: <b className="text-white">{b.ref}</b></li>
        <li>• {t("confDoctor")}</li>
        <li>• {t("confRegNo")}</li>
        <li>• {t("confDateTime")}: <b>{b.date} · {b.time}</b></li>
        {paid && (
          <li>• {t("confVideoLink")}: <a href={waDoctor} target="_blank" rel="noreferrer" data-testid="paid-whatsapp-link" className="text-emerald-300 underline underline-offset-4">{t("confWhatsappChat")}</a></li>
        )}
        <li>• {t("confFee")}: Rs {amount}</li>
        <li>• {t("confPaymentMode")}: {paymentMode}</li>
        <li>• {t("confPaymentProof")}: {b.payment?.ref && b.payment.ref !== "not provided" ? b.payment.ref : (b.payment_proof ? t("screenshotUploaded") : "-")}</li>
        {paid && <li>• {t("confGstLine")}</li>}
      </ul>

      {submitted && (
        <p className="mt-3 rounded-xl border border-amber-200/30 bg-amber-300/10 p-3 text-xs text-amber-100" data-testid="confirmation-pending-note">
          {t("pendingReceiptNote")}
        </p>
      )}

      {unpaid && (
        <div className="mt-3 rounded-xl border border-red-300/40 bg-red-500/10 p-3 text-xs text-red-100" data-testid="confirmation-unpaid-note">
          <p>{t("unpaidInstruction")}</p>
          <Link to="/edit-booking" data-testid="complete-payment-link" className="mt-2 inline-flex rounded-full border border-red-300/50 bg-red-500/30 px-4 py-1.5 text-xs text-white hover:bg-red-500/50">
            {t("completePaymentCta")}
          </Link>
        </div>
      )}

      <div className="mt-4 rounded-xl border border-emerald-300/15 bg-black/30 p-3 text-xs leading-relaxed text-emerald-100/80" data-testid="session-instructions">
        <p className="mb-1 font-semibold text-emerald-50">{t("sessionInstructionsTitle")}</p>
        <p>• {t("sessionInstruction1")}</p>
        <p>• {t("sessionInstruction2")}</p>
      </div>
    </div>
  );
}

export default function StepConfirm() {
  const { settings, state, update, reset, t, pick } = useApp();
  const [b, setB] = useState(state.booking);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!state.booking) return update({ step: 3 });
    api
      .get(`/bookings/${state.booking.id}`, { params: { token: state.booking.token } })
      .then(({ data }) => (OK.includes(data.status) ? setB(data) : update({ step: 3 })))
      .catch(() => update({ step: 1, booking: null }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open WhatsApp once per booking so patients who skip the tap still notify the doctor.
  // Keyed by booking id in localStorage so reload / later visit doesn't re-open it.
  // NOTE: hook must stay above any early return so React sees a stable hook order every render.
  useEffect(() => {
    if (!b || !OK.includes(b.status) || state.sent) return;
    const key = `jm_auto_wa_${b.id}`;
    try {
      if (window.localStorage.getItem(key)) return;
      window.localStorage.setItem(key, "1");
    } catch (_e) { /* storage disabled - just skip */ return; }
    const msg = buildWhatsappText(settings.texts.whatsapp_prefill, b);
    const url = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(msg)}`;
    const w = window.open(url, "_blank");
    if (w) {
      update({ sent: true });
      api.post(`/bookings/${b.id}/mark-whatsapp-sent`, { token: state.booking.token }).catch(() => {});
    }
  }, [b?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!b || !OK.includes(b.status)) return <div className="glass h-64 animate-pulse rounded-3xl" />;
  const doctorMsg = buildWhatsappText(settings.texts.whatsapp_prefill, b);
  const waUrl = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(doctorMsg)}`;
  const finishHint = pick(settings.texts, "finish_hint") || t("finishBookingHint");

  const finish = () => {
    if (state.sent || sending) return;
    setSending(true);
    const w = window.open(waUrl, "_blank");
    if (!w) {
      // popup blocked - keep the button active so the person can retry
      setSending(false);
      window.alert("Please allow pop-ups for this site, or tap the Finish booking button again.");
      return;
    }
    update({ sent: true });
    api.post(`/bookings/${b.id}/mark-whatsapp-sent`, { token: state.booking.token }).catch(() => {});
    setTimeout(() => { try { if (w && !w.closed) w.close(); } catch (_e) { /* ignored */ } }, 1500);
  };

  return (
    <section className="glass space-y-6 rounded-[2rem] p-6 sm:p-10" data-testid="step-confirm">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-emerald-300" />
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-amber-200/80">Ref {b.ref}</p>
          <h2 className="mt-1 font-display text-2xl text-white">{t("msgDoctor")}</h2>
        </div>
      </div>

      <PatientConfirmation b={b} settings={settings} t={t} />

      <div className="rounded-2xl border border-white/10 bg-black/25 p-5">
        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-xl bg-[#0b2a1f] p-4 font-sans text-sm text-emerald-50/90" data-testid="whatsapp-prefill">{doctorMsg}</pre>
        <Button
          data-testid="finish-booking-btn"
          disabled={state.sent || sending}
          onClick={finish}
          className="mt-4 h-12 w-full rounded-full bg-[#25D366] text-base text-[#06120e] hover:bg-[#3ee07c] disabled:opacity-60"
        >
          <Send className="mr-2 h-4 w-4" /> {state.sent ? t("bookingSent") : t("finishBooking")}
        </Button>
        <p
          data-testid="finish-booking-hint"
          className={`mt-3 rounded-xl border px-3 py-2 text-center text-xs leading-relaxed ${
            state.sent
              ? "border-emerald-300/25 bg-emerald-400/10 text-emerald-100/80"
              : "border-red-300/40 bg-red-500/10 text-red-100"
          }`}
        >
          {state.sent ? t("bookingSentHint") : finishHint}
        </p>
      </div>

      {state.sent && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="rounded-3xl border border-amber-200/25 bg-gradient-to-b from-amber-100/[0.08] to-transparent p-6 sm:p-8"
          data-testid="final-message">
          <p className="whitespace-pre-line font-display text-lg leading-relaxed text-white">
            {settings.texts.final_message?.en?.replace("{name}", b.name) || `Dear ${b.name}, your appointment is confirmed.`}
          </p>
        </motion.div>
      )}
      {state.sent && (
        <div className="text-center">
          <Button variant="ghost" data-testid="new-booking-btn" onClick={reset} className="rounded-full text-emerald-100 hover:bg-white/10">
            {t("newBooking")}
          </Button>
        </div>
      )}
    </section>
  );
}
